<script lang="ts">
  import { flip } from "svelte/animate";
  import { fade } from "svelte/transition";
  import { createEventDispatcher } from "svelte";
  import { fadeSlide } from "$transitions";
  import Form from "$components/form/Form/Form.svelte";
  import InputField from "$components/form/Fields/InputField.svelte";
  import CheckBoxesField from "$components/form/Fields/CheckBoxesField.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import OptionGroup from "$components/+design-system/Dropdown/fields/OptionGroup.svelte";
  import CheckboxOption from "$components/+design-system/Dropdown/fields/CheckboxOption.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import IconButton from "$components/+design-system/IconButton/IconButton.svelte";
  import { sineInOut } from "svelte/easing";
  import type { IconType } from "$components/+shared/OVIcon/OVIcon";
  import { AddGroup, Cog, Cross, Down } from "$components/+shared/OVIcon";

  export let tools: Array<{ title: string; icon: IconType; value: string }> = [];

  let toolsDropdownOpen = false;
  let submitting = false;

  const dispatch = createEventDispatcher();

  function onSubmit(ev: CustomEvent<any>) {
    dispatch("submit", {
      ...ev.detail,
      ...tools.reduce<{ [key: string]: boolean }>((map, tool) => {
        map[tool.value] = true;
        return map;
      }, {}),
    });
    submitting = true;
  }
</script>

<Form class="flex flex-col items-start gap-3 p-8" on:submit={onSubmit}>
  <div class="text-sm font-medium text-gray-500 dark:text-gray-300">
    Which tools do you use to connect with your customers?
  </div>
  <div>
    <div class="mb-3">
      <Dropdown bind:open={toolsDropdownOpen}>
        <Button
          let:active
          variant={active ? "active" : "secondary"}
          size="large"
          slot="trigger"
          iconLeft={AddGroup}
          iconRight={Down}
          label="Select tools"
        />
        <OptionGroup bind:values={tools} allowEmpty>
          <CheckboxOption
            title="Slack"
            key={(t) => t.value}
            value={{ title: "Slack", icon: Cog, value: "usedSlack" }}
          />
          <CheckboxOption
            title="Intercom"
            key={(t) => t.value}
            value={{ title: "Intercom", icon: Cog, value: "usedIntercom" }}
          />
          <CheckboxOption
            title="Zendesk"
            key={(t) => t.value}
            value={{ title: "Zendesk", icon: Cog, value: "usedZendesk" }}
          />
          <CheckboxOption
            title="Discourse"
            key={(t) => t.value}
            value={{ title: "Discourse", icon: Cog, value: "usedDiscourse" }}
          />
          <CheckboxOption
            title="Discord"
            key={(t) => t.value}
            value={{ title: "Discord", icon: Cog, value: "usedDiscord" }}
          />
          <CheckboxOption
            title="Hubspot"
            key={(t) => t.value}
            value={{ title: "Hubspot", icon: Cog, value: "usedHubspot" }}
          />
          <CheckboxOption
            title="Front"
            key={(t) => t.value}
            value={{ title: "Front", icon: Cog, value: "usedFront" }}
          />
          <CheckboxOption
            title="Other"
            key={(t) => t.value}
            value={{ title: "Other", icon: Cog, value: "usedOther" }}
          />
        </OptionGroup>
      </Dropdown>
    </div>

    <!-- Tool badges -->
    {#if tools.length}
      <div
        transition:fadeSlide={{ duration: 200, easing: sineInOut }}
        class="flex w-full flex-wrap items-center gap-1.5 pb-2"
      >
        {#each tools as tool (tool.title)}
          <div
            transition:fade|global={{ duration: 100 }}
            animate:flip={{ duration: 200 }}
            class="flex items-center gap-2 rounded bg-gray-200 py-1 pl-3 pr-2 text-sm font-medium dark:bg-white/5"
          >
            <span class="text-gray-500 dark:text-gray-300">{tool.title}</span>
            <IconButton
              icon={Cross}
              a11yLabel="Remove"
              variant="tertiary"
              size="small"
              on:click={() => (tools = tools.filter((f) => f.value != tool.value))}
            />
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <InputField
    name="currentDocTool"
    label="Link to your current customer support docs / help center"
    wrapperClassname="w-full mt-1"
    placeholder="e.g. https://help.outverse.com"
    validationRules={{
      pattern: {
        value: /^(https?:\/\/).*/,
        message: "You should submit a full url",
      },
    }}
  />

  <CheckBoxesField
    options={[{ label: "Subscribe to news & updates", value: false, id: "subscribe" }]}
  />

  <Button
    size="large"
    htmlButtonType="submit"
    variant="system"
    isFullWidth
    disabled={submitting}
    label={submitting ? "Saving..." : "Next"}
  />
</Form>
